import React, { useId } from 'react'

export interface ReferralJourneyProps {
  /**
   * Optional step labels rendered under the path (used by the in-app hero:
   * share → friend subscribes → both rewarded). Omit on the public marketing
   * band, where the three steps are rendered as separate cards below instead.
   */
  labels?: [string, string, string]
}

/**
 * The reusable animated referral "journey": a dotted track with three nodes and
 * a token that travels along it, lighting each node in turn. Pure declarative
 * animation — CSS `@keyframes refNodeLit` (kiao-app.css) drives the node glow and
 * SVG `<animateMotion>` drives the token — so no JS timers run. Shared by the
 * in-app {@link ReferralHero} and the public referral page's "how it works"
 * band, on a dark (ink) background.
 */
export function ReferralJourney({ labels }: ReferralJourneyProps) {
  // Unique per instance so two journeys on one page can't collide on the mpath
  // target id (`:` is invalid in a URL fragment, so strip it from useId()).
  const trackId = `${useId().replace(/:/g, '')}-track`
  return (
    <div className="journey">
      <svg
        className="journey-svg"
        viewBox="0 0 600 44"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <path id={trackId} className="track" d="M30 22 H570" />
        <circle className="node n1 lit" cx="30" cy="22" r="6" />
        <circle className="node n2 lit" cx="300" cy="22" r="6" />
        <circle className="node n3 lit" cx="570" cy="22" r="8" />
        <circle className="token" r="4">
          <animateMotion
            dur="3.6s"
            repeatCount="indefinite"
            keyPoints="0;0;0.5;0.5;1;1"
            keyTimes="0;0.08;0.42;0.5;0.84;1"
            calcMode="linear"
          >
            {/* xlinkHref alongside href: WebKit (desktop Tauri WKWebView / Safari)
                honors only the xlink-prefixed attribute on <mpath>. */}
            <mpath href={`#${trackId}`} xlinkHref={`#${trackId}`} />
          </animateMotion>
        </circle>
      </svg>
      {labels ? (
        <div className="journey-labels">
          <span>{labels[0]}</span>
          <span>{labels[1]}</span>
          <span>{labels[2]}</span>
        </div>
      ) : null}
    </div>
  )
}
